import Highcharts from "highcharts";
import { HighchartsReact } from "highcharts-react-official";
import highchartsHistogram from "highcharts/modules/histogram-bellcurve";
import { useContext, useMemo, useRef } from "react";

import {
  LineGraphEdrLineAllOfColorEnum,
  LineGraphWidgetConfiguration,
  LineGraphWidgetData,
} from "@/lib/api";
import { useSelectedTimeRange } from "@/features/dashboard/store";
import {
  BLUE,
  colorGenerator,
  getFormattedValue,
  getYAxisPerUnit,
  GREEN,
} from "@/features/shared/widgets/graphs/helpers";
import { useThresholdsY } from "@/features/shared/widgets/graphs/hooks/useThresholdsY";
import { useThresholdsX } from "@/features/shared/widgets/graphs/hooks/useThresholdsX";
import { WidgetContentContext } from "@/features/dashboard/components/widget-container-card";
import { useChartSizes } from "@/features/shared/widgets/graphs/hooks/useChartSizes";
import { NoDataOverlay } from "@/features/shared/widgets/graphs/no-data-label";
import { useLineGraphNoData } from "@/features/shared/widgets/graphs/hooks/use-line-graph-no-data";
import { usePrimaryUnit } from "@/features/shared/widgets/graphs/hooks/usePrimaryUnit";
import useLayoutStore from "@/store/useLayoutStore";

import baseOptions from "./base-options";

highchartsHistogram(Highcharts);

interface HistogramAdditionalProps {
  min?: Date;
  max?: Date;
}

interface HistogramProps {
  data: LineGraphWidgetData;
  configuration: LineGraphWidgetConfiguration & HistogramAdditionalProps;
  options?: Highcharts.Options;
  currentTime?: Date;
}

const Histogram = ({ data, configuration, options, currentTime }: HistogramProps) => {
  const chartRef = useRef(null);
  const widgetContentContext = useContext(WidgetContentContext);
  const widgetContentRef = widgetContentContext.ref;

  const [isFullscreen] = useLayoutStore((s) => s.context.isFullscreen);

  const chartHeight = useChartSizes(chartRef, widgetContentRef, isFullscreen ?? false);

  const timeRange = useSelectedTimeRange();
  const timeStart = timeRange.timeStart;
  const timeEnd = timeRange.timeEnd;

  // Horizontal line and band plots
  const [yThresholdLines, yThresholdPlots] = useThresholdsY(configuration);
  // calculate plot bands for x axis (where points exceed thresholds)
  const [xThresholdPlots, xThresholdLines] = useThresholdsX(configuration, data);

  const primaryUnit = usePrimaryUnit(data);
  const primaryUnitType = useMemo(
    () => (primaryUnit?.type === "UNKNOWN" ? primaryUnit.symbol : primaryUnit?.type),
    [primaryUnit],
  );

  const sortedLines = useMemo(() => {
    const priorityMap: { [key: string]: number } = {
      nc: 0,
      nc1: 3,
      nc2: 2,
      nc3: 1,
    };

    return data.lines?.sort((a, b) => {
      const aParam = a.data?.parameter.id ?? "";
      const aPrio = priorityMap[aParam] ?? 0;
      const bParam = b.data?.parameter.id ?? "";
      const bPrio = priorityMap[bParam] ?? 0;
      return aPrio - bPrio;
    });
  }, [data]);

  const chartData: Highcharts.Options | undefined = useMemo(() => {
    if (!data || !configuration) return undefined;

    const yAxis = getYAxisPerUnit(primaryUnitType);
    yAxis[0] = {
      ...baseOptions.yAxis,
      ...yAxis[0],
      title: {
        text: primaryUnit?.symbol,
      },
      plotLines: yThresholdLines,
      plotBands: yThresholdPlots,
    };

    if (options?.yAxis) {
      Array.isArray(options.yAxis)
        ? options.yAxis.forEach((axis, i) => (yAxis[i] = { ...yAxis[i], ...axis }))
        : (yAxis[0] = { ...yAxis[0], ...options.yAxis });
    }

    // Create generators for lines to get the rights colors
    const greenGenerator = colorGenerator(GREEN);
    const blueGenerator = colorGenerator(BLUE);
    const getColors = (key: LineGraphEdrLineAllOfColorEnum) => {
      if (key === LineGraphEdrLineAllOfColorEnum.Green) {
        return greenGenerator.next().value;
      }
      if (key === LineGraphEdrLineAllOfColorEnum.Blue) {
        return blueGenerator.next().value;
      }

      return undefined;
    };

    return {
      ...baseOptions,
      series: sortedLines?.map((line) => {
        const lineConfig = configuration.lines.find((config) => config.id == line.id);
        const points = line.data?.datapoints.filter((point) => point.value !== null) ?? [];
        const color =
          points.length === 0
            ? "grey"
            : getColors(lineConfig?.color ?? LineGraphEdrLineAllOfColorEnum.Blue);

        return {
          type: "column",
          name:
            points.length === 0
              ? `${lineConfig?.title} [not available]`
              : lineConfig?.title,
          description: line.data?.parameter.description,
          data:
            line.data?.datapoints.map((datapoint) => [
              +new Date(datapoint.timestamp),
              getFormattedValue(datapoint.value),
            ]) ?? [],
          tooltip: {
            valueSuffix: line.data?.parameter.unit.symbol,
          },
          color,
        };
      }),
      plotOptions: {
        column: {
          grouping: false,
          groupPadding: 0,
        },
      },
      xAxis: {
        type: "datetime",
        min: timeStart ? +timeStart : undefined,
        max: timeEnd ? +timeEnd : undefined,

        startOfWeek: 0,
        plotLines: currentTime
          ? [
              {
                id: "selectedTimeStart",
                color: "#FF0000",
                value: +currentTime,
                zIndex: 4,
              },
              ...xThresholdLines,
            ]
          : xThresholdLines,
        plotBands: xThresholdPlots,
      },
      yAxis,
      chart: {
        ...baseOptions.chart,
        height: chartHeight,
        ...options?.chart,
      },
    } as Highcharts.Options;
  }, [data, configuration, timeStart, timeEnd, sortedLines, chartHeight, primaryUnit]);

  const noData = useLineGraphNoData(data);

  return (
    <NoDataOverlay active={noData}>
      <HighchartsReact
        ref={chartRef}
        highcharts={Highcharts}
        options={chartData}
        containerProps={{
          style: {
            height: "100%",
            width: "100%",
          },
        }}
      />
    </NoDataOverlay>
  );
};

export default Histogram;
